import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { invalidateFor } from "@/lib/cache-registry";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import { logEvent } from "@/hooks/use-event-log";
import { isProjectLockedError, toastProjectLocked } from "@/lib/db-errors";

interface UpdateParams {
  timeEntryId: string;
  projectId: string;
  patch: Partial<{
    start_date: string;
    user_id: string;
    assignment_id: string | null;
    duration_seconds: number;
    task_description: string;
    comment: string | null;
  }>;
  oldValue?: unknown;
}

/**
 * Slå opp budget_line_id fra assignment_id. Returnerer null hvis assignment
 * ikke finnes, så vi ikke skriver en dinglende FK videre.
 */
async function resolveBudgetLineId(assignmentId: string): Promise<string | null> {
  const { data, error } = await supabase
    .from("budget_line_assignments")
    .select("budget_line_id")
    .eq("id", assignmentId)
    .maybeSingle();
  if (error) throw new Error(error.message);
  return data?.budget_line_id ?? null;
}

export function useUpdateTimeEntry() {
  const qc = useQueryClient();
  const { activeUser } = useImpersonation();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: async ({ timeEntryId, patch }: UpdateParams) => {
      // Admin Timer er SOT for FK-koblingen: når assignment_id endres må
      // budget_line_id følge med, ellers blir Timetildeling og Team&eierskap
      // hengende på en utdatert/null linje-referanse.
      const finalPatch: Record<string, unknown> = { ...patch };
      if ("assignment_id" in patch) {
        finalPatch.budget_line_id = patch.assignment_id
          ? await resolveBudgetLineId(patch.assignment_id)
          : null;
      }
      const { error } = await supabase
        .from("time_entries")
        .update(finalPatch)
        .eq("id", timeEntryId);
      if (error) throw new Error(error.message);
    },
    onSuccess: async (_data, vars) => {
      await invalidateFor(qc, "time_entries", {
        projectId: vars.projectId,
        userId: vars.patch.user_id,
      });
      logEvent({
        event_type: "admin.time_entry.updated",
        entity_type: "time_entry",
        entity_id: vars.timeEntryId,
        project_id: vars.projectId,
        user_id: activeUser?.id,
        payload: { patch: vars.patch, old: vars.oldValue },
      });
    },
    onError: (err, vars) => {
      if (isProjectLockedError(err)) {
        toastProjectLocked({ projectId: vars.projectId, navigate });
        return;
      }
      toast.error(`Kunne ikke lagre: ${(err as Error).message}`);
    },
  });
}

interface DeleteParams {
  timeEntryId: string;
  projectId: string;
}

export function useSoftDeleteTimeEntry() {
  const qc = useQueryClient();
  const { activeUser } = useImpersonation();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: async ({ timeEntryId }: DeleteParams) => {
      const { error } = await supabase
        .from("time_entries")
        .update({ deleted_at: new Date().toISOString() })
        .eq("id", timeEntryId);
      if (error) throw new Error(error.message);
    },
    onSuccess: async (_data, vars) => {
      await invalidateFor(qc, "time_entries", { projectId: vars.projectId });
      logEvent({
        event_type: "admin.time_entry.deleted",
        entity_type: "time_entry",
        entity_id: vars.timeEntryId,
        project_id: vars.projectId,
        user_id: activeUser?.id,
      });
    },
    onError: (err, vars) => {
      if (isProjectLockedError(err)) {
        toastProjectLocked({ projectId: vars.projectId, navigate });
        return;
      }
      toast.error(`Sletting feilet: ${(err as Error).message}`);
    },
  });
}

export function useRestoreTimeEntry() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: async ({ timeEntryId }: DeleteParams) => {
      const { error } = await supabase
        .from("time_entries")
        .update({ deleted_at: null })
        .eq("id", timeEntryId);
      if (error) throw new Error(error.message);
    },
    onSuccess: async (_data, vars) => {
      await invalidateFor(qc, "time_entries", { projectId: vars.projectId });
    },
    onError: (err, vars) => {
      if (isProjectLockedError(err)) {
        toastProjectLocked({ projectId: vars.projectId, navigate });
        return;
      }
      toast.error(`Gjenoppretting feilet: ${(err as Error).message}`);
    },
  });
}

interface CreateParams {
  projectId: string;
  user_id: string;
  start_date: string;
  duration_seconds: number;
  assignment_id: string | null;
  budget_line_id: string | null;
  task_description?: string;
}

export function useCreateTimeEntry() {
  const qc = useQueryClient();
  const { activeUser } = useImpersonation();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: async (params: CreateParams) => {
      // Hvis assignment_id er satt, la den styre budget_line_id så de to
      // alltid er konsistente. Caller-sendt budget_line_id ignoreres for å
      // unngå at en feilskrevet kombinasjon havner i DB.
      const budgetLineId = params.assignment_id
        ? await resolveBudgetLineId(params.assignment_id)
        : params.budget_line_id;
      const { data, error } = await supabase
        .from("time_entries")
        .insert({
          project_id: params.projectId,
          user_id: params.user_id,
          start_date: params.start_date,
          duration_seconds: params.duration_seconds,
          assignment_id: params.assignment_id,
          budget_line_id: budgetLineId,
          task_description: params.task_description ?? "Admin opprettet",
          status: "registrert",
        })
        .select()
        .single();
      if (error) throw new Error(error.message);
      return data;
    },
    onSuccess: async (data, vars) => {
      await invalidateFor(qc, "time_entries", {
        projectId: vars.projectId,
        userId: vars.user_id,
      });
      logEvent({
        event_type: "admin.time_entry.created",
        entity_type: "time_entry",
        entity_id: (data as { id: string }).id,
        project_id: vars.projectId,
        user_id: activeUser?.id,
        payload: { duration_seconds: vars.duration_seconds, user_id: vars.user_id },
      });
    },
    onError: (err, vars) => {
      if (isProjectLockedError(err)) {
        toastProjectLocked({ projectId: vars.projectId, navigate });
        return;
      }
      toast.error(`Opprettelse feilet: ${(err as Error).message}`);
    },
  });
}
